'use client';

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import Caution from '@alltrails/denali/icons/Caution';

const Dialog = dynamic(() => import('@alltrails/denali/components/Dialog'), { ssr: false });

const homePageDialogVariants = ['live-share-ended', 'live-share-expired'] as const;
type HomePageDialogVariant = typeof homePageDialogVariants[number];

const HomePageDialog = () => {
  const intl = useIntl();
  const searchParams = useSearchParams();
  const [variant, setVariant] = useState<HomePageDialogVariant | null>(null);

  const closeDialog = useCallback(() => {
    setVariant(null);
  }, []);

  useEffect(() => {
    const dialog = searchParams.get('dialog') as HomePageDialogVariant;
    if (dialog && homePageDialogVariants.includes(dialog)) {
      setVariant(dialog);
    }
  }, [searchParams]);

  switch (variant) {
    case 'live-share-ended':
      return (
        <Dialog
          body={intl.formatMessage({ defaultMessage: 'This member has stopped sharing their activity.' })}
          buttons={{
            primary: {
              onClick: closeDialog,
              testId: 'got-it',
              text: intl.formatMessage({ defaultMessage: 'Got it' })
            }
          }}
          icon={{ Component: Caution }}
          onRequestClose={closeDialog}
          size="md"
          title={intl.formatMessage({ defaultMessage: 'Live sharing ended' })}
        />
      );
    case 'live-share-expired':
      return (
        <Dialog
          body={intl.formatMessage({
            defaultMessage: 'Links are only good for 7 days. If the member is heading out soon, ask them to send you a new link.'
          })}
          buttons={{
            primary: {
              onClick: closeDialog,
              testId: 'got-it',
              text: intl.formatMessage({ defaultMessage: 'Got it' })
            }
          }}
          icon={{ Component: Caution }}
          onRequestClose={closeDialog}
          size="md"
          title={intl.formatMessage({ defaultMessage: 'Live share link has expired' })}
        />
      );
    default:
      return null;
  }
};

export default HomePageDialog;
